import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase, type AppRole } from "@/lib/supabase";

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  roles: AppRole[];
  isAdmin: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: set listener BEFORE getSession
    const { data: sub } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      setSession(newSession);
      
      if (newSession?.user) {
        // Se acabou de logar, registra esta sessão como a ÚNICA ativa no banco
        if (event === 'SIGNED_IN') {
          await supabase
            .from('profiles')
            .update({ active_session_id: newSession.access_token.slice(-20) }) // Usamos o final do token como ID único de sessão
            .eq('id', newSession.user.id);
        }

        setTimeout(() => {
          loadRoles(newSession.user.id).finally(() => setLoading(false));
        }, 0);
      } else {
        setRoles([]);
        setLoading(false);
      }
    });

    // Check para Sessão Única (Anti-Sharing)
    const checkSession = async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession?.user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('active_session_id')
        .eq('id', currentSession.user.id)
        .single();

      // Se o ID no banco for diferente do token atual, significa que outro dispositivo logou
      if (profile?.active_session_id && profile.active_session_id !== currentSession.access_token.slice(-20)) {
        console.warn("Sessão encerrada: Outro dispositivo detectado.");
        await supabase.auth.signOut();
        setSession(null);
        setRoles([]);
        window.location.href = "/login?error=session_conflict";
      }
    };

    // Verifica a cada 30 segundos e ao trocar de aba
    const interval = setInterval(checkSession, 30000);
    window.addEventListener('focus', checkSession);

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      if (data.session?.user) {
        loadRoles(data.session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    return () => {
      sub.subscription.unsubscribe();
      clearInterval(interval);
      window.removeEventListener('focus', checkSession);
    };
  }, []);

  async function loadRoles(userId: string) {
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    if (!error && data) setRoles(data.map((r) => r.role as AppRole));
  }

  async function signOut() {
    // LGPD Art. 18 — destrói dados fiscais em memória antes de encerrar a sessão.
    try {
      const { useFiscalStore } = await import("@/store/fiscal-store");
      useFiscalStore.getState().purgeAllData();
    } catch {
      /* store ainda não carregado — sem dados a purgar */
    }
    await supabase.auth.signOut();
    setSession(null);
    setRoles([]);
  }

  const value: AuthContextValue = {
    session,
    user: session?.user ?? null,
    roles,
    isAdmin: roles.includes("admin"),
    loading,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
